import { useState } from 'react';

import { api } from '../api/client';
import type { RoleRow } from '../api/types';
import { Badge, Button, Card, Loading, Modal, Notice, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * What each role may do.
 *
 * A role is a named set of permissions and nothing else. Nowhere in this product does
 * code ask "is this user role 7" — SMA did exactly that throughout, which is the single
 * thing that made it unservable for a second client whose role structure differed (D1).
 *
 * Seeded roles cannot be deleted, because the bootstrap account and the seeder depend on
 * them. Their permission sets are fully editable, because what a client's reviewer may do
 * is a client's decision.
 */
export function Roles(): JSX.Element {
  const { can } = useSession();
  const [editing, setEditing] = useState<RoleRow | null>(null);

  const roles = useResource(() => api.get<RoleRow[]>('/roles'), []);
  const permissions = useResource(
    () =>
      can('role.edit')
        ? api.get<Array<{ key: string; description: string }>>('/permissions')
        : Promise.resolve([]),
    [],
  );

  return (
    <>
      <Card title="Roles">
        {roles.loading ? (
          <Loading />
        ) : roles.error !== null ? (
          <Notice kind="error">{roles.error}</Notice>
        ) : (
          <Table
            rows={roles.data ?? []}
            keyOf={(row) => row.id}
            onRowClick={can('role.edit') ? (row) => setEditing(row) : undefined}
            columns={[
              { header: 'Role', render: (row) => row.name },
              { header: 'Key', render: (row) => <code>{row.key}</code> },
              { header: 'People', numeric: true, render: (row) => row.userCount },
              { header: 'Permissions', numeric: true, render: (row) => row.permissions.length },
              {
                header: '',
                render: (row) => (row.isSystem ? <Badge tone="low">Built in</Badge> : null),
              },
            ]}
          />
        )}
      </Card>

      {editing !== null && (
        <PermissionEditor
          role={editing}
          available={permissions.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            roles.reload();
          }}
        />
      )}
    </>
  );
}

function PermissionEditor({
  role,
  available,
  onClose,
  onSaved,
}: {
  role: RoleRow;
  available: Array<{ key: string; description: string }>;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [selected, setSelected] = useState<Set<string>>(new Set(role.permissions));

  const toggle = (key: string): void => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const save = async (): Promise<void> => {
    // Replacement, not a merge: the set on screen is the set that results. Merging would
    // leave a permission the administrator just unticked quietly in place.
    const ok = await run(async () => {
      await api.put(`/roles/${role.id}/permissions`, { permissions: [...selected] });
    });
    if (ok) onSaved();
  };

  // Grouped by the part before the dot — the domain the permission belongs to.
  const groups = new Map<string, Array<{ key: string; description: string }>>();
  for (const permission of available) {
    const area = permission.key.split('.')[0] ?? 'other';
    groups.set(area, [...(groups.get(area) ?? []), permission]);
  }

  return (
    <Modal title={`${role.name} — permissions`} onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}

      {role.isSystem && (
        <Notice kind="info">
          This is a built-in role. It cannot be deleted, but what it may do is yours to set.
        </Notice>
      )}

      <div className="stack">
        {[...groups.entries()].map(([area, entries]) => (
          <div key={area}>
            <div className="sidebar-group">{area}</div>
            <div className="permission-grid">
              {entries.map((permission) => (
                <label key={permission.key} title={permission.description}>
                  <input
                    type="checkbox"
                    checked={selected.has(permission.key)}
                    onChange={() => toggle(permission.key)}
                  />
                  <span>{permission.description}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="form-actions">
          <Button onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : `Save ${selected.size} permission(s)`}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
